"use client";

import { type FC, type Ref, useMemo } from "react";
import { ActionCard } from "@huub-web/ui/components/ActionCard";
import type { UIAction } from "@huub-web/ui/types/UIAction";
import { useActions } from "@huub-web/ui/hooks/useActions";

import { useTemplates } from "../../hooks";
import { usePageContent } from "../../hooks/usePageContent";
import type { TemplateIcon } from "../../types/TemplateIcon";

export interface ActionCardTemplateProps {
  /**
   * Markdown enabled
   */
  title: string;
  /**
   * Markdown enabled
   */
  body?: string;
  /**
   * The button value of the card
   */
  value?: string;
  /**
   * The action to perform when the card is clicked
   */
  action?: UIAction;
  /**
   * The icon to display on the button of the card
   */
  buttonIcon?: TemplateIcon;
  /**
   * If the card is disabled
   */
  disabled?: boolean;
}

export const ActionCardTemplate: FC<
  ActionCardTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({ title, body, value, disabled, buttonIcon, action, ref }) => {
  const { lockPrompts, lockUI } = useTemplates();
  const { handleAction } = useActions();

  const contentItem = useMemo(
    () =>
      title
        ? {
            type: "card" as const,
            label: title,
            ...(body ? { description: body } : {}),
            ...(action ? { action } : {}),
          }
        : null,
    [title, body, action],
  );
  usePageContent("actioncard", contentItem);

  const handleClick = () => {
    if (action) {
      handleAction(action);
    }
  };

  const isDisabled = (() => {
    if (action?.type === "prompt") {
      return lockPrompts;
    }
    return lockUI || disabled;
  })();

  return (
    <ActionCard
      ref={ref}
      title={title}
      body={body}
      value={value}
      onClick={handleClick}
      disabled={isDisabled}
      variant={action?.type === "prompt" ? "prompt" : undefined}
      buttonIcon={buttonIcon}
    />
  );
};
